import { useCallback, useMemo, useState } from "react";
import { type FacetValue } from "../../../../models/models";
import FilterGroupItem from "../../FilterGroupItem";

import { Button, Checkbox, FormControlLabel, List, ListItem, Stack, styled, TextField, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { C_ON_SURFACE_LOW_EMPHASIS } from "~/common/components/icon/Colors";
import type { FacetFilterData } from "~/features/search/models/filterConfigModels";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { toggleFacet } from "~/features/search/redux/actions";
import { selectFacetValueByCode } from "~/features/search/redux/searchSelectors";

interface ReduxFacetFilterProps {
  data: FacetFilterData;
  hidden?: boolean;
}

export const FacetFilter = ({ data, hidden }: ReduxFacetFilterProps) => {
  const facetValues = useSelector((state: RootState) => selectFacetValueByCode(state, data.code));
  const dispatch = useDispatch();

  return (
    <BaseFacetFilter
      data={data}
      values={facetValues.values}
      onToggleFacet={(facetValueCode, facetCategoryCode) => dispatch(toggleFacet(facetValueCode, facetCategoryCode))}
      hidden={hidden}
    />
  );
};

interface Props {
  data: FacetFilterData;
  values: FacetValue[];
  onToggleFacet: (facetValueCode: string, facetCategoryCode: string) => void;
  hidden?: boolean;
}

const VISIBLE_COUNT = 8;
const MAX_PLACEHOLDER_LENGTH = 20;

const truncate = (text: string, max: number) => (text.length > max ? `${text.slice(0, max)}…` : text);

const BaseFacetFilter = (props: Props) => {
  const { title, initiallyExpanded } = props.data;
  const { values } = props;

  const toExpand = initiallyExpanded || values.some((facetValue) => facetValue.selected);

  return (
    <FilterGroupItem title={title} initiallyExpanded={toExpand} data-test-id={props.data.code} hidden={props.hidden}>
      <FacetFilterContent {...props} />
    </FilterGroupItem>
  );
};

const FacetFilterContent = ({ data, values, onToggleFacet: externalOnToggle }: Props) => {
  const { t } = useTranslation();
  const { title, code: facetCategoryCode } = data;

  const [showAll, setShowAll] = useState<boolean>(false);
  const [search, setSearch] = useState("");

  const onToggleFacet = useCallback(
    (facetCode: string) => {
      externalOnToggle(facetCode, facetCategoryCode);
    },
    [externalOnToggle, facetCategoryCode],
  );

  // 🔍 Filter values as user types
  const filteredValues = useMemo(() => {
    if (!search.trim()) {
      return values;
    }

    const lower = search.toLowerCase();
    return values.filter((v) => v.name.toLowerCase().includes(lower));
  }, [values, search]);

  const visibleValues = showAll || search ? filteredValues : filteredValues.slice(0, VISIBLE_COUNT);

  const showSearch = values.length > VISIBLE_COUNT;

  const placeholder = t("searchPage.refinement.filter.searchPlaceholder", {
    name: truncate(title, MAX_PLACEHOLDER_LENGTH),
  });

  return (
    <Stack gap={1}>
      {showSearch && (
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
        />
      )}

      <List>
        {visibleValues.map((value) => (
          <StyledListItem key={value.code}>
            <FacetFilterValue {...value} onToggle={onToggleFacet} />
          </StyledListItem>
        ))}

        {search && filteredValues.length === 0 && (
          <StyledListItem>{t("searchPage.refinement.filter.noResults")}</StyledListItem>
        )}
      </List>
      {!search && values.length > VISIBLE_COUNT && (
        <Button
          type="button"
          variant="text"
          onClick={() => setShowAll((prev) => !prev)}
          sx={{
            alignSelf: "flex-start",
          }}
        >
          {showAll ? t("searchPage.refinement.filter.showLess") : t("searchPage.refinement.filter.showMore")}
        </Button>
      )}
    </Stack>
  );
};

export const InnerFacetFilter = ({ data }: { data: FacetFilterData }) => {
  const facetValues = useSelector((state: RootState) => selectFacetValueByCode(state, data.code));
  const dispatch = useDispatch();

  return (
    <FacetFilterContent
      data={data}
      values={facetValues.values}
      onToggleFacet={(facetValueCode, facetCategoryCode) => dispatch(toggleFacet(facetValueCode, facetCategoryCode))}
    />
  );
};

const StyledListItem = styled(ListItem)(() => ({
  minHeight: "40px",
}));

interface FacetFilterValueProps extends FacetValue {
  onToggle: (facetCode: string) => void;
}

const FacetFilterValue = ({ code, onToggle, count, name, selected }: FacetFilterValueProps) => {
  const cb = useCallback(() => {
    onToggle(code);
  }, [code, onToggle]);
  const { t } = useTranslation();
  const translatedName = t(name);

  const label = (
    <Typography variant={"labelMedium"} component={"span"}>
      {translatedName}
      <Counter> ({count})</Counter>
    </Typography>
  );

  return (
    <FormControlLabel
      sx={{ marginBlock: "-8px" }}
      control={<Checkbox checked={selected} onChange={cb} data-test-id={`facet-filter-${code}`} />}
      label={label}
    />
  );
};

const Counter = styled("span")({
  color: C_ON_SURFACE_LOW_EMPHASIS,
  whiteSpace: "pre",
});
